import React, { useState, useEffect } from 'react';
import { Box } from 'utils/style/extendable';
import { withDataContext, IDataContext } from 'store/context/DataContext';
import { IOption } from 'components/Select/Select';
import {
  Title,
  SideSetting,
  TitleItem,
  IconItem,
  ListItem,
  ReportButton,
  SideBody,
} from './styles';
import messages from './Messages';
import SavedReportList from './SavedReports/SavedReportList';
import BaseDatePicker, { ILocal } from './BaseDatePicker';
import ChartSettingEditor from './ChartSettingEditor';
import ReportExplorerModal from './ReportExplorerModal';
import Icon from 'components/Icon';
import ReportConfig from './ReportConfig';
import ReportAutoGenerate from './ReportAutoGenerate';
import APopover from '../APopover';
import notify from '../Notify';
import {
  IProviders,
  ITags,
  ITaskReportInfo,
} from 'store/providers/providerTypes';
import AutoReportList from './SavedReports/AutoReportList';
import {
  DashboardLink,
  IProviderSurveyReport,
  IQuestionReportField,
  IReportView,
  ISurveyItem,
  ISurveyReportMap,
  ISurveyViewWithData,
  ITagReportField,
  TimeScale,
} from 'store/survey/surveyTypes';
import { IOpenItem } from 'store/entity/IOpenItem';
import {
  ICompany,
  ICompanyProviders,
} from '../../store/companies/companiesTypes';
import LocalIcon from '../../ui/LocalIcon';
import { black } from '../../utils/style/colors';
import { useTranslation } from 'react-i18next';
import { getCalendarLocal } from '../../i18n';
import { TReportField } from './ReportFields';

interface IComponentProps {
  baseDate: Date;
  canUpdate: boolean;
  canUpdateCheckpoint: boolean;
  totalTime: boolean;
  dataContext: IDataContext;
  editing: boolean;
  editType?: string;
  fields: Array<ITagReportField | IQuestionReportField>;
  loadAllReports: (reports: ISurveyReportMap) => void;
  loadingAnalyse: boolean;
  onChangeTimeScale: (value: TimeScale) => void;
  onChangeTimeLength: (value: string) => void;
  onChangeInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeTags: (tags: IOption[]) => void;
  onChangeField: (index: number, field: TReportField) => void;
  onChooseAReport: (reportId: number | null, type: string) => void;
  onRemoveField: (questionId: number, subQuestionId?: string) => void;
  onSaveReport: () => void;
  addOrUpdateTaskReport: (taskReport: ITaskReportInfo) => void;
  onGenerateReport: (
    questionId: number | undefined,
    options: any
  ) => Promise<any>;
  onChangeBaseDate: (baseDate: Date) => void;
  onCopyReport: (reportId: number) => void;
  onDeleteReport: (reportId: number) => void;
  onPrepareReport: (reportId?: number, report?: IReportView) => void;
  providers: IProviders;
  reportName: string;
  reportTags: IOption[];
  searchedReportIds: number[];
  providerTags: ITags;
  reports: ISurveyReportMap;
  saveReport: (report: IReportView) => void;
  saveReportItem: (item: IProviderSurveyReport) => void;
  savingReport: boolean;
  selectedReport: number | null;
  splitOnTime: boolean;
  dashboardWidth: number;
  dashboardHeight: number;
  dashboardLinks: DashboardLink[];
  onLinksChanged: (tags: DashboardLink[]) => void;
  survey?: ISurveyViewWithData;
  isOwnerOrSupport?: boolean;
  surveyReport?: IOpenItem;
  surveyItem?: ISurveyItem;
  oldVersion?: boolean;
  timeScale: string;
  timeLength: string;
  viewOnlyInMainOwner: boolean;
  similarPastYearPeriodLength: number;
  updateSurveyReportItem: (item: IProviderSurveyReport) => Promise<any>;
  deleteReportsInBulk: (reportListId: number[]) => void;
  flag: string;
  onChangeFlag: (event: React.ChangeEvent<HTMLInputElement>) => void;
  company?: ICompany;
}

const ChartSetting: React.FC<IComponentProps> = (props) => {
  const [state, setState] = useState({
    showFileExplorer: false,
    showCheckPointModal: false,
    showConfig: false,
    showQuickAdd: false,
    showCopy: false,
    autoReportList: [] as IReportView[],
  });

  const [providers, setProviders] = useState({} as ICompanyProviders);
  const { t } = useTranslation('survey');
  const local = (
    props.surveyItem?.providerId
      ? props.providers.map?.[props.surveyItem.providerId]?.data
          ?.surveyOwnerProperties.localId || getCalendarLocal()
      : getCalendarLocal()
  ) as ILocal;

  useEffect(() => {
    const providerId =
      props.surveyItem?.providerId ||
      props.dataContext.providerId ||
      props.surveyReport?.item.data.providerId;
    if (providerId) {
      const provider = props.providers.map?.[providerId]?.data;
      const p = {};
      p[providerId] = {
        id: providerId,
        name: provider?.name ?? '',
        rootDocumentId: provider?.rootDocument?.id,
      };
      setProviders(p);
    }
  }, [props.dataContext.providerId, props.providers]);

  const handleShowSaved = (state: boolean) => () => {
    setState((prevState) => ({
      ...prevState,
      showConfig: false,
      showQuickAdd: false,
    }));
    props.onChooseAReport(state ? null : -1, state ? 'analyse' : 'edit');
  };

  const handleShowQuickAdd = () => {
    setState({
      showConfig: false,
      showQuickAdd: true,
      showCheckPointModal: false,
      showFileExplorer: false,
      showCopy: false,
      autoReportList: state.autoReportList,
    });
    props.onChooseAReport(null, 'analyse');
  };

  const handleShowConfig = () => {
    setState((prevState) => ({
      ...prevState,
      showConfig: true,
    }));
  };

  const handleShowFileExplorer = () => {
    setState((prevState) => ({
      ...prevState,
      showFileExplorer: !prevState.showFileExplorer,
      showConfig: false,
      showCopy: false,
    }));
  };

  const copyTaskReport = (content: ITaskReportInfo) => {
    content!.id = undefined;
    props.addOrUpdateTaskReport(content);
  };

  const handlePasteTaskReportFromClipboard = () => {
    setState((prevState) => ({
      ...prevState,
      showCopy: false,
    }));
    navigator.clipboard.readText().then(
      (text) => {
        try {
          const content = JSON.parse(text) as ITaskReportInfo & {
            templateName: string;
            partName: string;
          };
          const found = content && content.companyId && content.templateId;
          const surveyReport = props.surveyReport;
          const providerId = surveyReport!.item.data.providerId;
          const relatedCompanyId =
            props.providers.map[providerId].data!.managingCompanyId;
          if (found && content.companyId !== relatedCompanyId)
            notify(t('notRelatedCompany'), 'warning');
          else if (found) {
            const confirm = messages.copyContent(
              content.templateName,
              content.partName
            );
            const result = window.confirm(confirm);
            content.order =
              [
                ...Object.values(props.reports),
                ...((props.surveyReport?.item.data as IProviderSurveyReport)
                  .surveyReportLinks ?? []),
                ...((props.surveyReport?.item.data as IProviderSurveyReport)
                  .taskReportList ?? []),
              ].reduce((current, item) => {
                return Math.max(current, item.order);
              }, 0) + 1;

            content.name =
              content.name || content.partName + ' ' + content.templateName;
            if (result) copyTaskReport(content);
          } else {
            notify(t('noItemFound'), 'warning');
          }
        } catch (e) {
          notify(t('noItemFound'), 'warning');
        }
      },
      () => {
        notify(t('noItemFound'), 'warning');
      }
    );
  };

  const providerTags =
    props.providerTags.data &&
    Object.values(props.providerTags.data).reduce((tmp: IOption[], tag) => {
      const label = tag.text;
      tmp.push({ value: label, label });
      return tmp;
    }, []);

  const isChecklist = !!props.surveyItem?.tags.find(
    (it) =>
      it.text === 'چک‌‌لیست' ||
      it.text === 'چکلیست' ||
      it.text === 'چک‌لیست' ||
      it.text === 'چک لیست'
  );

  const reportsTags = Object.values(props.reports ?? {}).reduce(
    (tmp: any, report) => {
      report.tags.forEach((tag: string) => {
        // avoid push duplicate tags
        if (tmp.find((t: IOption) => t.label === tag)) {
          return;
        }
        tmp.push({ value: tag, label: tag });
      });
      return tmp;
    },
    []
  );

  let allTags;
  if (providerTags) {
    allTags = [...providerTags, ...reportsTags];
  } else {
    allTags = [...reportsTags];
  }

  const { showConfig, showQuickAdd, showCopy, showFileExplorer } = state;

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '8px',
        overflowY: 'auto',
      }}
    >
      <Box style={{ marginBottom: '1rem' }}>
        <BaseDatePicker
          baseDate={props.baseDate}
          onChangeBaseDate={props.onChangeBaseDate}
          sendDateOnTabChange={true}
          showDaySelect={true}
          local={local}
        />
      </Box>
      <SideSetting fullHeight={true}>
        <Title>
          <TitleItem
            onClick={handleShowSaved(true)}
            active={!props.editing && !showConfig && !showQuickAdd}
          >
            {t('reports')}
          </TitleItem>
          {!props.oldVersion && (
            <>
              <TitleItem
                onClick={handleShowSaved(false)}
                active={
                  props.editing && props.selectedReport === -1 && !showConfig
                }
              >
                {t('new')}
              </TitleItem>
              {props.canUpdate && props.survey && props.isOwnerOrSupport && (
                <TitleItem onClick={handleShowQuickAdd} active={showQuickAdd}>
                  {t('quickAdd')}
                </TitleItem>
              )}
            </>
          )}
          {props.surveyReport && (
            <>
              <APopover
                arrow={true}
                curveBorder={true}
                show={showCopy}
                trigger="click"
                onShowChange={() =>
                  setState((prevState) => ({
                    ...prevState,
                    showCopy: !showCopy,
                  }))
                }
                target={
                  <IconItem>
                    <LocalIcon
                      iconName="duplicate.svg"
                      type="sm"
                      color={black}
                    />
                  </IconItem>
                }
              >
                <div>
                  <ListItem onClick={handleShowFileExplorer}>
                    <Icon type="check" />
                    <span>{t('copySurveyReport')}</span>
                  </ListItem>
                  <ListItem onClick={handlePasteTaskReportFromClipboard}>
                    <Icon type="clipboard" />
                    <span>{t('pasteTaskReport')}</span>
                  </ListItem>
                </div>
              </APopover>
              <TitleItem onClick={handleShowConfig} active={showConfig}>
                {/*<LocalIcon iconName="setting.svg" type="sm" color={black} />*/}
                <Icon type="settings" />
              </TitleItem>
            </>
          )}
        </Title>
        <SideBody editing={props.editing}>
          {props.editing ? (
            <ChartSettingEditor
              allTags={allTags}
              editType={props.editType}
              canUpdate={props.canUpdate}
              fields={props.fields}
              loading={props.loadingAnalyse}
              onChangeField={props.onChangeField}
              onChangeInput={props.onChangeInput}
              onChangeTags={props.onChangeTags}
              onChangeTimeScale={props.onChangeTimeScale}
              onChangeTimeLength={props.onChangeTimeLength}
              onPrepareReport={props.onPrepareReport}
              onRemoveField={props.onRemoveField}
              onSaveReport={props.onSaveReport}
              report={props.reports[props.selectedReport!]}
              reportName={props.reportName}
              reportTags={props.reportTags}
              splitOnTime={props.splitOnTime}
              dashboardWidth={props.dashboardWidth}
              dashboardHeight={props.dashboardHeight}
              dashboardLinks={props.dashboardLinks}
              onLinksChanged={props.onLinksChanged}
              survey={props.survey}
              savingReport={props.savingReport}
              surveyReport={props.surveyReport}
              timeScale={props.timeScale}
              totalTime={props.totalTime}
              timeLength={props.timeLength}
              surveyItem={props.surveyItem}
              similarPastYearPeriodLength={props.similarPastYearPeriodLength}
              flag={props.flag}
              onChangeFlag={props.onChangeFlag}
              viewOnlyInMainOwner={props.viewOnlyInMainOwner}
              company={props.company}
            />
          ) : showConfig && props.surveyReport ? (
            <ReportConfig
              tags={props.providerTags}
              surveyReport={props.surveyReport}
              updateSurveyReportItem={props.updateSurveyReportItem}
            />
          ) : showQuickAdd && props.survey ? (
            <ReportAutoGenerate
              survey={props.survey}
              onGenerateReport={props.onGenerateReport}
            />
          ) : (
            <>
              {props.survey && props.surveyItem && isChecklist && (
                <>
                  {/*<h5>{messages.autoReports}</h5>*/}
                  <AutoReportList
                    canUpdate={props.canUpdate}
                    isActive={!props.editing}
                    onPrepareReport={props.onPrepareReport}
                    onCopyReport={props.onCopyReport}
                    onDeleteReport={props.onDeleteReport}
                    reportItem={props.surveyReport}
                    reportsTags={reportsTags}
                    saveReport={props.saveReport}
                    selectedReport={props.selectedReport}
                    survey={props.survey.data}
                    surveyItem={props.surveyItem}
                    oldVersion={props.oldVersion}
                    showExtraReports={props.isOwnerOrSupport}
                    providerId={
                      (props.surveyItem?.providerId ||
                        props.surveyReport?.item.data.providerId)!
                    }
                    setAutoReportList={(list) => {
                      setState((prevState) => ({
                        ...prevState,
                        autoReportList: list,
                      }));
                    }}
                    // deleteReportsInBulk={props.deleteReportsInBulk}
                  />
                </>
              )}
              <SavedReportList
                canUpdate={props.canUpdate}
                canUpdateCheckpoint={props.canUpdateCheckpoint}
                isActive={!props.editing}
                onChooseAReport={props.onChooseAReport}
                onCopyReport={props.onCopyReport}
                onDeleteReport={props.onDeleteReport}
                reportItem={props.surveyReport}
                reports={props.reports}
                reportsTags={reportsTags}
                saveReport={props.saveReport}
                saveReportItem={props.updateSurveyReportItem}
                selectedReport={props.selectedReport}
                survey={props.survey?.data}
                surveyItem={props.surveyItem}
                oldVersion={props.oldVersion}
                searchedReportIds={props.searchedReportIds}
                providerId={
                  (props.surveyItem?.providerId ||
                    props.surveyReport?.item.data.providerId)!
                }
                deleteReportsInBulk={props.deleteReportsInBulk}
              />
              {Object.keys(props.reports).length + state.autoReportList.length >
              0 ? (
                <ReportButton
                  color="green"
                  onClick={() => {
                    const allReports = props.reports;
                    state.autoReportList.forEach((report) => {
                      allReports[report.id] = report;
                    });
                    props.loadAllReports(allReports);
                  }}
                >
                  {t('cumulativeReport')}
                </ReportButton>
              ) : (
                <></>
              )}
            </>
          )}
        </SideBody>
      </SideSetting>
      {showFileExplorer && props.surveyReport && (
        <ReportExplorerModal
          onClose={handleShowFileExplorer}
          providers={providers}
          itemId={props.surveyReport.item.data.id}
          providerId={props.surveyReport.item.data.providerId}
        />
      )}
    </div>
  );
};

export default withDataContext(ChartSetting);
